/**
 * IndicatorManualModal — Modal com manual dos indicadores contextual por perfil
 */
import { BookOpen, X } from "lucide-react";
import { useState } from "react";

type Profile = "salvia" | "channel" | "rh";

interface IndicatorDef {
  name: string;
  formula: string;
  description: string;
  range?: string;
  source?: string;
}

const INDICATORS: Record<Profile, IndicatorDef[]> = {
  rh: [
    { name: "OWI — Occupational Wellbeing Index", formula: "OWI = Σ(moduleScore × weight) / Σ(weights)", description: "Índice composto de bem-estar ocupacional. Média ponderada dos módulos COPSOQ/Ergos avaliados.", range: "0 a 25", source: "evaluation_results_core" },
    { name: "Score Ergos (classificationScore)", formula: "Score = Σ(respostas × peso) por setor×cargo", description: "Pontuação de risco psicossocial calculada pelo motor Ergos. Quanto maior, maior o risco.", range: "0 a 25", source: "evaluation_classification_results" },
    { name: "Taxa de Adesão", formula: "Adesão = (respostas completas / total pendências) × 100", description: "Percentual de colaboradores que completaram o questionário.", range: "0% a 100%", source: "questionnaire_submissions / evaluation_pendings" },
    { name: "Custo do Não-Agir", formula: "Custo = vidas_em_risco × 10% × R$3.500/mês × 12", description: "Estimativa anual do custo de afastamentos. Premissa: 10% dos colaboradores em risco elevado se afastam.", range: "R$ 0+", source: "Cálculo interno" },
    { name: "Qualificador de Risco", formula: "Aceitável (<5), Atenção (5-11), Incerto (12-14), Inaceitável (≥15)", description: "Classificação qualitativa do nível de risco psicossocial por setor×cargo.", source: "evaluation_classification_results" },
    { name: "Carga Mental (mentalLoadScore)", formula: "mentalLoadScore = média das respostas do módulo de carga mental", description: "Score específico do módulo de carga mental/cognitiva do questionário Ergos.", range: "0 a 25", source: "evaluation_results_core" },
  ],
  channel: [
    { name: "Saúde da Conta", formula: "Saúde = (adesão×0.30) + (progresso×0.30) + (taxaDoc×0.20) + ((1-taxaBloqueio)×0.20)", description: "Índice composto que mede a saúde operacional de cada cliente.", range: "0 a 100", source: "Cálculo interno" },
    { name: "Adesão Média", formula: "Adesão = (total respostas / total pendências) × 100", description: "Percentual médio de respostas coletadas em relação ao esperado.", range: "0% a 100%", source: "questionnaire_submissions / evaluation_pendings" },
    { name: "Progresso", formula: "Progresso = (avaliações concluídas / total pendências) × 100", description: "Percentual de avaliações processadas com sucesso pelo motor Ergos.", range: "0% a 100%", source: "evaluation_pendings" },
    { name: "Etapa da Jornada", formula: "Não iniciado → Importação → Campanha → Coleta → Avaliação → Documento", description: "Estágio atual do cliente no ciclo NR1.", source: "Múltiplas tabelas" },
  ],
  salvia: [
    { name: "ISP — Índice de Saúde da Plataforma", formula: "ISP = (cobertura×0.25) + (progresso×0.25) + (avaliações×0.25) + (docs×0.25)", description: "Índice composto que mede a saúde geral da plataforma Sálvia.", range: "0% a 100%", source: "Cálculo interno" },
    { name: "INRP — Índice Nacional de Risco Psicossocial", formula: "INRP = (AVG(classificationScore) / 25) × 100", description: "Índice agregado de risco psicossocial de toda a base.", range: "0 a 100", source: "evaluation_classification_results" },
    { name: "Receita Estimada", formula: "Receita = Σ(employeesCount) × R$15/vida", description: "Proxy de receita baseado no número de vidas sob gestão.", range: "R$ 0+", source: "evaluation_pendings" },
    { name: "Maturidade do Canal", formula: "Score 0-7: ≥1 cliente, ≥5 clientes, 50%+ import, ≥1 campanha, 30%+ adesão, ≥1 avaliação, ≥1 doc", description: "Nível de maturidade operacional do canal parceiro.", range: "Iniciante / Operacional / Estratégico", source: "Múltiplas tabelas" },
    { name: "Funil do Ecossistema", formula: "Canais → Clientes → Vidas → Campanhas → Resultados → Documentos", description: "Funil de conversão do ecossistema completo.", source: "Múltiplas tabelas" },
  ],
};

interface Props {
  profile: Profile;
  className?: string;
}

export default function IndicatorManualModal({ profile, className = "" }: Props) {
  const [open, setOpen] = useState(false);
  const indicators = INDICATORS[profile] || [];

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={`fixed bottom-6 left-24 right-auto z-50 hidden items-center gap-2 rounded-full bg-[#002F59] px-4 py-2.5 text-white shadow-lg transition-colors hover:bg-[#001F3F] xl:flex ${className}`}
      >
        <BookOpen size={18} />
        <span className="text-sm font-medium">Manual dos Indicadores</span>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setOpen(false)}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b bg-[#002F59] text-white">
          <div className="flex items-center gap-2">
            <BookOpen size={20} />
            <h2 className="text-lg font-semibold">Manual dos Indicadores — {profile === "rh" ? "RH" : profile === "channel" ? "Canal" : "Sálvia"}</h2>
          </div>
          <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/20 rounded"><X size={20} /></button>
        </div>
        <div className="overflow-y-auto max-h-[calc(80vh-64px)] p-6 space-y-4">
          {indicators.map((ind, i) => (
            <div key={i} className="border rounded-lg p-4">
              <h3 className="font-semibold text-[#002F59] text-sm">{ind.name}</h3>
              <div className="mt-2 bg-slate-50 rounded px-3 py-2">
                <p className="text-xs font-mono text-slate-700">{ind.formula}</p>
              </div>
              <p className="text-xs text-muted-foreground mt-2">{ind.description}</p>
              <div className="flex gap-4 mt-2">
                {ind.range && <span className="text-xs text-slate-500">Range: {ind.range}</span>}
                {ind.source && <span className="text-xs text-slate-400">Fonte: {ind.source}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
